import {
  forwardRef,
  HTMLAttributes,
  memo,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
} from 'react';
import { BaseFilterProps } from './common';
import { FilterStringOptionDTO, StringFilterDTO } from '../../../api/generated';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { Box, Chip, Popper, styled, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

export type StringSelectFilterProps = BaseFilterProps<StringFilterDTO, FilterStringOptionDTO[]>;

export const StringSelectFilter = memo(function ({
  value,
  onValueChange,
  onCommit,
  filterOptions,
}: StringSelectFilterProps): ReactElement {
  const isDisabled = useMemo(() => !filterOptions, [filterOptions]);
  const optionsMap = useMemo(() => Object.fromEntries((filterOptions || []).map((v) => [v.value, v])), [filterOptions]);

  const handleChange = useCallback(
    function (event: SyntheticEvent, newValue: string[], reason: string): void {
      onValueChange(!!newValue && newValue.length ? { in: newValue } : undefined);
      if (reason === 'clear') {
        // commit right away if clear button was pressed
        onCommit();
      }
    },
    [onValueChange, onCommit]
  );

  return (
    <Autocomplete
      sx={{ margin: 'auto', width: '90%' }}
      multiple
      disableCloseOnSelect
      disableListWrap
      disabled={isDisabled}
      options={Object.keys(optionsMap)}
      value={value?.in || []}
      onChange={handleChange}
      onClose={onCommit}
      renderInput={(params) => <TextField {...params} size='small' fullWidth />}
      renderTags={(value, getTagProps) => {
        // disbale delete by removing onDelete from props
        return value.map((option, index) => {
          const tagProps = getTagProps({ index });
          return <Chip label={option} {...tagProps} key={tagProps.key} onDelete={undefined} />;
        });
      }}
      PopperComponent={StyledPopper}
      ListboxComponent={VirtualizedListbox}
      renderOption={(props, option) => [props, optionsMap[option]] as ReactNode}
    />
  );
});

const VirtualizedListbox = memo(
  forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(function (
    { children, ...outerProps }: PropsWithChildren,
    ref
  ) {
    // NOTE: list data is passed in the children properties. `renderOption` property on the `Autocomplete` renders each
    //    item in this array. This line tries to capture the type weirdness that comes with that.
    const itemData = children as [HTMLAttributes<HTMLLIElement> & { key: any }, FilterStringOptionDTO][];

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
    const itemSize = useMemo(() => (smUp ? 36 : 48), [smUp]);

    return (
      <div ref={ref}>
        <FixedSizeList
          outerElementType={
            forwardRef<HTMLDivElement>((props, ref) => {
              return <div ref={ref} {...props} {...outerProps} />;
            }) as any
          }
          innerElementType='ul'
          height={itemSize * 20} // estimated upper bound for visible height
          width='100%'
          itemData={itemData}
          itemSize={itemSize}
          itemCount={itemData.length}
          overscanCount={5}
        >
          {Row}
        </FixedSizeList>
      </div>
    );
  })
);

const LISTBOX_PADDING = 8; // [px]

const Row = function ({ data, index, style }: ListChildComponentProps) {
  const [optionProps, optionData] = data[index] as [
    HTMLAttributes<HTMLLIElement> & { key: any },
    FilterStringOptionDTO,
  ];

  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return (
    <Box
      {...optionProps}
      key={optionProps.key}
      component='li'
      display='flex'
      flexDirection='row'
      justifyItems='space-between'
      alignItems='center'
      style={inlineStyle}
    >
      <Typography noWrap flex={1} textOverflow='hidden' textAlign='left'>
        {optionData.value}
      </Typography>
      <Typography noWrap flex={1} textAlign='end'>
        {optionData.count ?? 0}
      </Typography>
    </Box>
  );
};
